import { ENGINE_WORKSCOPE_LEVELS } from '../ProcessingData.js';
import z from 'zod';
/**
 * Engine shop-visit workscope events for a project (DR-3126).
 *
 * Emitted as ONE combined webhook per project — every Engine-Workscope document's
 * consolidated events are packed into a single `workscopeEvents` array so consumers
 * can draw a single maintenance-history table. This is the Bill-of-Sale UNION shape,
 * not the AD/SB/QEC winning-document pick: distinct shop visits are separate facts
 * and none of them may be dropped. The same visit repeated across duplicate uploads
 * is collapsed upstream by the cross-document dedupe on normalized `shopVisitRef`.
 *
 * Because the payload spans multiple source documents the source lives on each event
 * (`documentId` + `reconstructedDocumentId` + `pageIds`), not at the top level.
 *
 * Every workscope field is OPTIONAL by design. The extractor is tri-state honest (a
 * field the page does not print is omitted, never guessed) and consolidation drops a
 * scalar the contributing pages disagree on, so an event legitimately carries only
 * the facts that are settled. `workscopeLevelRaw` / `dateRaw` / `dateLabel` are the
 * evidence lanes behind `workscopeLevel` and `date`. `workOrders` (DR-4360) is the set
 * of printed shop order numbers, unioned across pages — the key a consumer matches
 * against a release certificate's work order. Engine-header `tsn` / `csn` /
 * `tslsv` / `cslsv` (DR-3507) are optional clocks for placing an undated event;
 * they are omitted when the page did not print an engine-owned header clock.
 * `moduleLevels` (DR-5967) is the level printed for one module ("Level 3 Overhaul" on a
 * one-module sheet), each paired with a `modulesWorked` entry; it is the OEM module
 * scale, never the visit `workscopeLevel`, and is absent when no module level is known.
 */
export const workscopeEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('workscope-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `pma-der-events-ready` /
    // `document-header-ready` rather than the looser `project-progress` / `project-data-ready`
    // spelling.
    //
    // Required. Brain always emits this stamp. An envelope without it does not parse (DR-3341).
    generatedAt: z.iso.datetime(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    workscopeEvents: z.array(z.looseObject({
        shopVisitRef: z.string().optional(),
        // Printed shop order numbers (Work Order / W.O. / PM / Shop / Sales order), each
        // verbatim (DR-4360). A set unioned across pages, never a visit key.
        workOrders: z.array(z.string()).optional(),
        // Derived deterministically from `workscopeLevelRaw` by the sanitiser, never
        // taken from the model's own enum pick.
        workscopeLevel: z.enum(ENGINE_WORKSCOPE_LEVELS).optional(),
        workscopeLevelRaw: z.string().optional(),
        modulesWorked: z.array(z.string()).optional(),
        // Per-module level as printed (DR-5967); each `module` is a `modulesWorked` entry.
        // Absent, never `[]`, when no module level is known.
        moduleLevels: z.array(z.looseObject({ module: z.string(), levelRaw: z.string() })).optional(),
        date: z.string().optional(),
        dateRaw: z.string().optional(),
        dateLabel: z.string().optional(),
        facility: z.string().optional(),
        // Engine-header clocks only (DR-3507). Finite and non-negative: a bad extract
        // cannot pass NaN, Infinity, or a negative hour/cycle total. Cycles are whole counts.
        tsn: z.number().finite().nonnegative().optional(),
        csn: z.number().int().nonnegative().optional(),
        tslsv: z.number().finite().nonnegative().optional(),
        cslsv: z.number().int().nonnegative().optional(),
        documentId: z.string(),
        // The stable reconstructed-document id of the contributing entity, and every
        // page that contributed to the event (unioned when the cross-document dedupe
        // merged copies of the same visit).
        reconstructedDocumentId: z.string(),
        pageIds: z.array(z.string()),
    })),
});
//# sourceMappingURL=workscope-events-ready.webhook.js.map